/**
 * QuickPatientSearch — global top-bar patient search, always visible as an input (it used to be an
 * icon that opened a dialog). Searches by name or full 10-digit mobile (indexed blind-index search)
 * as you type, and opens the patient window on selection. Ctrl+K (⌘K) focuses it from anywhere.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Box, CircularProgress, ClickAwayListener, InputAdornment, InputBase, List, ListItemButton, ListItemText,
  Paper, Popper, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search, X } from 'lucide-react';
import { api } from '../../lib/api';
import { usePatientDetails } from '../../contexts/PatientDetailsContext';
import { normalizeArray } from '../../lib/utils';
import type { Patient } from '../../types';
import { useTranslation } from 'react-i18next';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 350;
/** Keyboard hint shown in the empty field — a key name, not translatable text. */
const SHORTCUT_HINT = 'Ctrl K';

export function QuickPatientSearch() {
  const { t } = useTranslation('ui');
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { openPatient } = usePatientDetails();
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestSeq = useRef(0);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const digits = query.replace(/\D/g, '');
  const numericQuery = digits.length > 0 && digits.length === query.replace(/\s/g, '').length;
  const partialNumber = numericQuery && digits.length < 10;

  const runSearch = async (q: string) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await api.patients.search(q);
      if (seq !== requestSeq.current) return; // a newer keystroke superseded this request
      setResults(res.error ? [] : normalizeArray<Patient>(res.data || []));
    } catch {
      if (seq === requestSeq.current) setResults([]);
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
        setSearched(true);
        setActive(-1);
      }
    }
  };

  // Search as you type; a partial mobile number waits for all 10 digits (the index needs the full number).
  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_CHARS || partialNumber) {
      requestSeq.current++;
      setResults([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    const timer = setTimeout(() => { void runSearch(q); }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, partialNumber]);

  // Ctrl+K / ⌘K focuses the search from anywhere in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const reset = () => {
    requestSeq.current++;
    setQuery('');
    setResults([]);
    setSearched(false);
    setLoading(false);
    setActive(-1);
  };

  const select = (p: Patient) => {
    reset();
    setOpen(false);
    inputRef.current?.blur();
    openPatient(p.id, p);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && results.length) {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp' && results.length) {
      e.preventDefault();
      setActive((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      if (active >= 0 && results[active]) select(results[active]);
      else if (query.trim().length >= MIN_CHARS && !partialNumber) void runSearch(query.trim());
    } else if (e.key === 'Escape') {
      if (query) reset();
      else { setOpen(false); inputRef.current?.blur(); }
    }
  };

  const showPanel = open && query.trim().length > 0;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ flex: { xs: 1, sm: '0 1 340px' }, minWidth: { xs: 120, sm: 220 }, maxWidth: 420 }}>
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={t('nameOrFull10DigitMobile')}
          inputProps={{
            'aria-label': t('findPatientNameOrFull10'),
            role: 'combobox',
            'aria-expanded': showPanel,
            'aria-controls': 'quick-patient-search-results',
            'aria-autocomplete': 'list',
          }}
          startAdornment={
            <InputAdornment position="start" sx={{ ml: 1, color: 'text.secondary' }}>
              {loading ? <CircularProgress size={14} /> : <Search size={16} />}
            </InputAdornment>
          }
          endAdornment={query ? (
            <InputAdornment position="end" sx={{ mr: 0.5 }}>
              <Box component="button" type="button" onClick={() => { reset(); inputRef.current?.focus(); }}
                aria-label={t('clearSearch')}
                sx={{ all: 'unset', cursor: 'pointer', display: 'flex', p: 0.5, borderRadius: 1, color: 'text.secondary',
                  '&:hover': { color: 'text.primary' }, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}>
                <X size={14} />
              </Box>
            </InputAdornment>
          ) : (
            <Box component="kbd" sx={{ display: { xs: 'none', md: 'block' }, mr: 1, px: 0.75, py: 0.1, fontSize: '0.68rem',
              fontFamily: 'inherit', color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 0.75,
              whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1.6 }}>
              {SHORTCUT_HINT}
            </Box>
          )}
          sx={{
            width: '100%',
            height: 36,
            fontSize: '0.85rem',
            borderRadius: 2,
            border: '1px solid',
            borderColor: open ? 'primary.main' : 'divider',
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
            transition: 'border-color .15s',
            '& input::placeholder': { opacity: 0.75 },
          }}
        />

        {/* At least 300px wide (phones get a narrow field), right-aligned to it and kept on screen. */}
        <Popper open={showPanel} anchorEl={anchorRef.current} placement="bottom-end"
          modifiers={[{ name: 'preventOverflow', options: { padding: 8 } }]}
          style={{
            width: Math.min(Math.max(anchorRef.current?.offsetWidth ?? 0, 300), window.innerWidth - 16),
            zIndex: theme.zIndex.modal + 1,
          }}>
          <Paper id="quick-patient-search-results" elevation={6} sx={{ mt: 0.75, borderRadius: 2, overflow: 'hidden', maxHeight: 380, overflowY: 'auto' }}>
            {partialNumber && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                {t('mobileSearchNeedsFullNumber')}
              </Typography>
            )}
            {!partialNumber && query.trim().length < MIN_CHARS && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>{t('typeTwoCharactersToSearch')}</Typography>
            )}
            {!partialNumber && results.length > 0 && (
              <List dense role="listbox" disablePadding>
                {results.map((p, i) => (
                  <ListItemButton key={p.id} role="option" selected={i === active} aria-selected={i === active}
                    onMouseEnter={() => setActive(i)} onClick={() => select(p)}>
                    <ListItemText
                      primary={p.name}
                      secondary={[p.age != null ? `${p.age}y` : null, p.gender, p.mobile].filter(Boolean).join(' · ')}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
            {!partialNumber && !loading && searched && results.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                {t('noMatchingPatientFound')}
              </Typography>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
